import React, { useState, useEffect, useCallback } from "react";

import { Card } from "./ui/card";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import WithdrawAddBank from "./WithdrawAddBank";
import Deposit from "./Deposit";
import { useToast } from "../hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import {
  verify2FA,
  createBankAccount,
  withdraw,
  withdrawQuote,
  refreshPortfolioAndAssets,
} from "../services/digitalWalletService";
import {
  refreshBalanceQueries,
  refreshBusinessWalletQueries,
} from "../services/invalidator";
import {
  postBusinessFiatWithdrawalsCreate,
  postBusinessFiatWithdrawalsGetQuote,
} from "../services/businessWalletService";
import { useBusinessWalletAssetsData } from "../hooks/query/businessWalletQueries";
import {
  useEightyEightBanks,
  useFiatAssets,
  useFiatBalance,
  useUserBanking,
} from "../hooks/query/queries";
import { useFeatureAccess, FeatureGate } from "./FeatureGate";
import { useAuth } from "../context/AuthContext";
import BankDetails from "./BankDetails";
import AuthTwoFA from "./AuthTwoFA";
import BankAddComplete from "./BankAddComplete";
import WithdrawConfirm from "./ConfirmWithdraw";
import WithdrawalComplete from "./WithdrawalComplete";
import DepositMade from "./DepositMade";
import WithdrawPage from "./WithdrawPage";
import { SecuredFooter } from "./ui/secured-footer";
import TransactionFailure from "./TransactionFailure";
import { FIAT_EXPRESS_WITHDRAWAL_MIN_ZAR } from "../constants/fiatWithdrawal";
import { useBusinessFiatSelection } from "../hooks/useBusinessFiatSelection";

export default function WithdrawParent({ tab, childTab, businessId }) {
  const { flags: featureFlags } = useFeatureAccess("mainFeatures", [
    "withdraw",
    "deposit",
  ]);
  const [amount, setAmount] = useState(null);
  const [instantPayment, setInstantPayment] = useState(true);
  const [minWithdrawal, setMinWithdrawal] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const [viewAccDetails, setViewAccDetails] = useState(false);
  const [open, setOpen] = useState(false);
  const [twoFA, setTwoFA] = useState(false);
  const [showAddBank, setShowAddBank] = useState(false);
  const [addComplete, setAddComplete] = useState(false);
  const [isWithdrawal, setIsWithdrawal] = useState(false);
  const [showDefault, setShowDefault] = useState(true);
  const [confirmWithdrawal, setConfirmWithdrawal] = useState(false);
  const [withdrawalComplete, setWithdrawalComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [flagExceed, setFlagExceed] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [showDepositMadePage, setShowDepositMadePage] = useState(false);
  const [fiatBalance, setFiatBalance] = useState(null);
  const [selectedFiat, setSelectedFiat] = useState(null);
  const [depositBank, setDepositBank] = useState([]);
  const [hasBeenSetUp, setHasBeenSetUp] = useState(false);
  const [flagSelectedBank, setFlagSelectedBank] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [withdrawData, setWithdrawData] = useState(null);
  const [selectedTab, setSelectedTab] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    if (selectedTab !== null) return;
    if (featureFlags?.withdraw) {
      setSelectedTab("withdraw");
    } else if (featureFlags?.deposit) {
      setSelectedTab("deposit");
    }
  }, [featureFlags, selectedTab]);

  const getValidSelectedTab = () => {
    if (selectedTab === "withdraw" || selectedTab === "deposit") {
      return selectedTab;
    }
    if (featureFlags?.withdraw) {
      return "withdraw";
    }
    if (featureFlags?.deposit) {
      return "deposit";
    }
    return "withdraw";
  };
  const [bankFormState, setBankFormState] = useState({
    selectedBank: null,
    newBankType: "",
    newAccountNumber: "",
    newBranchCode: "",
  });
  const [transactionError, setTransactionError] = useState(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const { data: fiat, isLoading: isLoadingFiat } = useFiatAssets();
  const { data: businessWalletData, isLoading: isLoadingBusinessWalletAssets } =
    useBusinessWalletAssetsData(businessId || null);
  const { toast } = useToast();
  const { userProfile, settings, updateSettings, refreshUser } = useAuth();
  const { data } = useFiatBalance(businessId ? null : selectedFiat);
  const queryClient = useQueryClient();
  const refCode = userProfile?.ref_info?.user_code;
  const userId = userProfile?._id;
  const { data: existingbanks, isLoading: loadingExisting } =
    useUserBanking(userId);

  const { data: banks } = useEightyEightBanks();

  const getErrorMessage = (error) => {
    if (typeof error === "string") return error;
    if (error?.response?.data?.message) return error.response.data.message;
    if (error?.message) return error.message;
    return "Something went wrong. Please try again.";
  };

  const isRetryable = (error) => {
    if (error?.response?.status >= 500) return true;
    if (
      error?.message?.includes("Network") ||
      error?.message?.includes("timeout")
    )
      return true;
    return false;
  };

  useEffect(() => {
    setIsEnabled(settings?.security?.twoFactorAuth?.enabled);
  }, [settings]);

  useEffect(() => {
    const handleFocus = () => {
      if (refreshUser && userProfile) {
        refreshUser();
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [refreshUser, userProfile]);

  useEffect(() => {
    setDepositBank(banks);
  }, [banks]);

  const isInWithdrawSubFlow =
    confirmWithdrawal ||
    withdrawalComplete ||
    transactionError ||
    twoFA ||
    viewAccDetails ||
    showAddBank ||
    addComplete;
  useEffect(() => {
    if (existingbanks?.length > 0) {
      setShowAddBank((prev) => (isInWithdrawSubFlow ? prev : false));
      setShowDefault((prev) => (isInWithdrawSubFlow ? prev : true));
    }
    if (existingbanks?.length <= 0) {
      setShowAddBank(true);
      setShowDefault(false);
    }
  }, [existingbanks, isComplete, isInWithdrawSubFlow]);

  useEffect(() => {
    const list = Array.isArray(existingbanks) ? existingbanks : [];
    if (list.length === 0) return;
    setSelectedBank((prev) => {
      if (!prev) return prev;
      const hasFullBank =
        prev.bankIconUrl != null && prev.bankIconUrl !== "" && prev._id;
      if (hasFullBank) return prev;
      const normalize = (v) => (v != null ? String(v).replace(/\s/g, "") : "");
      const prevAcc = normalize(prev.accNumber ?? prev.accountNumber);
      const match = list.find(
        (b) =>
          b._id === prev._id ||
          (prevAcc && normalize(b.accNumber ?? b.accountNumber) === prevAcc)
      );
      if (!match) return prev;
      return {
        ...match,
        bankName: match.bankName ?? match.bank,
        accNumber: match.accNumber ?? match.accountNumber,
        bankIconUrl: match.bankIconUrl ?? match.iconUrl,
      };
    });
  }, [existingbanks]);

  useEffect(() => {
    const validTabs = ["withdraw", "deposit"];
    if (!childTab) return;
    if (validTabs.includes(childTab)) {
      setSelectedTab(childTab);
    }
  }, [childTab]);

  useEffect(() => {
    const validTabs = ["withdraw", "deposit"];
    if (!tab) return;
    if (validTabs.includes(tab)) {
      setSelectedTab(tab);
    }
  }, [tab]);

  const { fiatOptions } = useBusinessFiatSelection({
    businessId,
    businessWalletData,
    fiat,
    selectedFiat,
    setSelectedFiat,
    setFiatBalance,
    setMinWithdrawal,
  });

  useEffect(() => {
    if (businessId) return;
    if (data) {
      setMinWithdrawal(data.minWithdrawal);
      setFiatBalance(data.availableAmount);
    }
  }, [data, businessId]);

  useEffect(() => {
    if (!amount || amount === "" || amount === null) {
      setFlagExceed(false);
      return;
    }

    if (!fiatBalance) {
      setFlagExceed(false);
      return;
    }

    const balanceStr = String(fiatBalance)
      .replace(/^(ZAR|R)\s*/i, "")
      .replace(/\s/g, "")
      .replace(/,/g, "");
    const cleanAmount = parseFloat(balanceStr) || 0;
    const amountNum = parseFloat(amount) || 0;

    const availableBalance = Math.max(0, cleanAmount);

    if (amountNum > availableBalance) {
      setFlagExceed(true);
    } else {
      setFlagExceed(false);
    }
  }, [amount, instantPayment, fiatBalance]);
  const withdrawalData = {
    type: "Withdrawal",
    asset: "zar",
    requestedAmount: withdrawData?.amountReceived,
    fees: 0,
    userId: userId,
    bankId: selectedBank?._id,
    immediatePayment: instantPayment,
  };
  const withdrawalQuoteData = {
    asset: "zar",
    requestedAmount: parseFloat(amount),
    immediatePayment: instantPayment,
  };
  const handleWithdrawQuote = async () => {
    setDisabled(true);
    setTransactionError(null);
    try {
      let result;
      if (businessId) {
        result = await postBusinessFiatWithdrawalsGetQuote(businessId, {
          asset: "zar",
          requestedAmount: parseFloat(amount, 10),
          immediatePayment: instantPayment,
        });
      } else {
        result = await withdrawQuote(withdrawalQuoteData);
      }
      const theDataWithdraw = result?.data?.data ?? result?.data ?? null;
      setWithdrawData(theDataWithdraw);
      setConfirmWithdrawal(true);
      setShowDefault(false);
    } catch (error) {
      const message = getErrorMessage(error);
      setTransactionError({
        title: "Withdrawal Quote Failed",
        message: message,
        retryable: isRetryable(error),
      });
      setShowDefault(false);
      setConfirmWithdrawal(false);

      toast({
        description: message,
        variant: "eighty",
      });
    } finally {
      setDisabled(false);
    }
  };
  const handleContinue = () => {
    setTransactionError(null);
    if (!isEnabled) {
      toast({
        description:
          "2-Factor Authentication is required to withdraw. Please enable 2FA in your settings.",
        variant: "eighty",
      });
      return;
    }

    if (!selectedBank) {
      setFlagSelectedBank(true);
      return;
    } else {
      setFlagSelectedBank(false);
    }

    if (!amount || parseFloat(amount) <= 0) {
      alert("Enter a valid amount.");
      return;
    }
    const amountNum = parseFloat(amount);

    if (instantPayment && amountNum < FIAT_EXPRESS_WITHDRAWAL_MIN_ZAR) {
      toast({
        description: `Amount must be at least R${FIAT_EXPRESS_WITHDRAWAL_MIN_ZAR} for express withdrawal`,
        variant: "eighty",
      });
      return;
    }

    if (amountNum < parseFloat(minWithdrawal)) {
      toast({
        description: `Minimum withdrawal cannot be less than ${parseFloat(
          minWithdrawal
        )?.toFixed(2)}`,
        variant: "eighty",
      });
      return;
    }
    handleWithdrawQuote();
  };
  const handleSelecedBank = (bank) => {
    setSelectedBank(bank);
  };

  const handleNewBank = (bank) => {
    setShowAddBank(false);
    setSelectedBank(bank);
    setViewAccDetails(true);
  };

  const handleBankFormStateChange = (updates) => {
    setBankFormState((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  const handleTwoFA = () => {
    if (hasBeenSetUp) {
      addBankAcc();
    }
    if (!hasBeenSetUp) {
      setViewAccDetails(false);
      setTwoFA(true);
    }
  };

  const handleWithDrawTwoFA = () => {
    setIsWithdrawal(true);
    setConfirmWithdrawal(false);
    if (hasBeenSetUp) {
      createWithdrawal();
    }
    if (!hasBeenSetUp) {
      setTwoFA(true);
    }
  };

  const handleVerify = async (token) => {
    setIsVerifying(true);
    setTransactionError(null);
    try {
      const twoFAdata = {
        token: token,
      };

      const verifiedTwoFA = await verify2FA(twoFAdata);
      if (verifiedTwoFA) {
        updateSettings(verifiedTwoFA);
        toast({
          description: `2FA ${isEnabled ? "verified" : "enabled"} Successfully`,
          variant: "eighty",
        });
        if (!isWithdrawal) {
          addBankAcc();
        }
        if (isWithdrawal) {
          createWithdrawal();
        }
      } else {
        setIsVerifying(false);
        toast({
          description: "2FA verification failed. Please try again.",
          variant: "eighty",
        });
      }
    } catch (error) {
      setIsVerifying(false);
      const message = getErrorMessage(error);
      toast({
        description: message || "2FA verification failed. Please try again.",
        variant: "eighty",
      });
    }
  };

  const addBankAcc = async () => {
    setTransactionError(null);
    try {
      const result = await createBankAccount(selectedBank);

      if (result) {
        const createdBank = result?.data?.data ?? result?.data;
        if (createdBank && (createdBank._id || createdBank.bankIconUrl)) {
          setSelectedBank(createdBank);
        }
        setViewAccDetails(false);
        setTwoFA(false);
        setAddComplete(true);
        setIsVerifying(false);
      } else {
        setIsVerifying(false);
        setTransactionError({
          title: "Bank Account Error",
          message: "Failed to add bank account. Please try again.",
          retryable: true,
        });
        setViewAccDetails(false);
        setTwoFA(false);
        toast({
          description: "Failed to add bank account. Please try again.",
          variant: "eighty",
        });
      }
    } catch (error) {
      setIsVerifying(false);
      const message = getErrorMessage(error);
      setTransactionError({
        title: "Bank Account Error",
        message: message || "Failed to add bank account. Please try again.",
        retryable: isRetryable(error),
      });
      setViewAccDetails(false);
      setTwoFA(false);
      toast({
        description: message || "Failed to add bank account. Please try again.",
        variant: "eighty",
      });
    } finally {
      setLoading(false);
      queryClient.invalidateQueries({ queryKey: ["userBanking"] });
    }
  };

  const createWithdrawal = async () => {
    setTransactionError(null);
    setIsVerifying(true);
    try {
      let result;
      if (businessId) {
        result = await postBusinessFiatWithdrawalsCreate(businessId, {
          type: "withdrawal",
          quoteId: withdrawData?.quoteId ?? withdrawData?._id ?? "",
          bankId: selectedBank?._id,
          notes: "",
          txid: "",
          chainId: "",
        });
      } else {
        result = await withdraw(withdrawalData);
      }

      if (result) {
        setWithdrawalComplete(true);
        setTwoFA(false);
        setIsVerifying(false);
        setConfirmWithdrawal(false);
        // Refresh portfolio data first, then invalidate/refetch dependent queries
        await refreshPortfolioAndAssets();
        await refreshBalanceQueries(queryClient);
        await refreshBusinessWalletQueries(queryClient, businessId);
        queryClient.invalidateQueries({ queryKey: ["notifications"] });
      } else {
        setIsVerifying(false);
        setTransactionError({
          title: "Withdrawal Failed",
          message:
            "Your withdrawal request could not be processed. Please try again.",
          retryable: true,
        });
        setTwoFA(false);
        setConfirmWithdrawal(false);
        toast({
          description: "Withdrawal failed. Please try again.",
          variant: "eighty",
        });
      }
    } catch (error) {
      setIsVerifying(false);
      const message = getErrorMessage(error);
      setTransactionError({
        title: "Withdrawal Failed",
        message: message || "Withdrawal failed. Please try again.",
        retryable: isRetryable(error),
      });
      setTwoFA(false);
      setConfirmWithdrawal(false);
      toast({
        description: message || "Withdrawal failed. Please try again.",
        variant: "eighty",
      });
    }
  };

  const handleSelect = (asset) => {
    setOpen(false);

    setSelectedFiat(asset);
  };
  const handleAddBankComplete = useCallback(() => {
    setAmount(null);
    setFiatBalance(null);
    setSelectedFiat(null);
    setAddComplete(false);
    setShowDefault(true);
    setShowAddBank(false);
    setIsComplete((prev) => !prev);
    setBankFormState({
      selectedBank: null,
      newBankType: "",
      newAccountNumber: "",
      newBranchCode: "",
    });
  }, []);

  const handleWithdrawalComplete = useCallback(() => {
    setAmount(null);
    setFiatBalance(null);
    setSelectedFiat(null);
    setWithdrawalComplete(false);
    setIsComplete((prev) => !prev);
  }, []);

  const handleDepositComplete = useCallback(() => {
    setAmount(null);
    setFiatBalance(null);
    setSelectedFiat(null);
    setShowDepositMadePage(false);
    setIsComplete((prev) => !prev);
    setSelectedTab("withdraw");
  }, []);
  const handleBack = () => {
    setViewAccDetails(false);
    setShowAddBank(true);
  };
  const handleConfirmBack = () => {
    setConfirmWithdrawal(false);
    setShowDefault(true);
    setTransactionError(null);
  };
  const handleTabChange = (value) => {
    if (value === "withdraw" || value === "deposit") {
      setSelectedTab(value);
    }
  };
  const handleDepositMade = async () => {
    setShowDefault(false);
    setShowAddBank(false);
    setShowDepositMadePage(true);
    // Refresh portfolio data first, then invalidate/refetch dependent queries
    await refreshPortfolioAndAssets();
    await refreshBalanceQueries(queryClient);
  };

  const onAmountChange = (e) => {
    const val = e.target.value;
    if (!/^\d*\.?\d*$/.test(val)) return;

    setAmount(val);
  };
  const handleAddNewBank = () => {
    setShowAddBank(true);
    setShowDefault(false);
    setTransactionError(null);
  };
  const handleBackFromAddBank = () => {
    setShowAddBank(false);
    setShowDefault(true);
    setTransactionError(null);
    setBankFormState({
      selectedBank: null,
      newBankType: "",
      newAccountNumber: "",
      newBranchCode: "",
    });
  };
  const handleSetup = () => {
    setHasBeenSetUp(true);
  };
  const handleBackfromTwoFA = () => {
    setIsVerifying(false);
    setTwoFA(false);
    setTransactionError(null);
    if (isWithdrawal) {
      setConfirmWithdrawal(true);
    } else {
      setViewAccDetails(true);
    }
  };

  const handleTransactionErrorClose = () => {
    setTransactionError(null);
    if (isWithdrawal) {
      setConfirmWithdrawal(true);
    } else if (showAddBank) {
      setShowAddBank(true);
      setViewAccDetails(false);
    } else {
      setShowDefault(true);
    }
  };

  const handleTransactionErrorRetry = async () => {
    setIsRetrying(true);
    setTransactionError(null);

    try {
      if (isWithdrawal && confirmWithdrawal) {
        await createWithdrawal();
      } else if (isWithdrawal && !confirmWithdrawal) {
        await handleWithdrawQuote();
      } else if (viewAccDetails) {
        await addBankAcc();
      }
    } catch (error) {
    } finally {
      setIsRetrying(false);
    }
  };
  return (
    <>
      {showDefault && (
        <Card className="flex flex-col w-full lg:pt-8 pt-5 !shadow-none bg-white rounded-2xl">
          <div className="lg:px-8 px-5">
            <Tabs
              value={getValidSelectedTab()}
              onValueChange={handleTabChange}
              className="mb-8"
            >
              <TabsList
                className={`grid rounded-full bg-transparent border border-[#EBE9EF] border-[1px] h-[64px] px-[8px] ${featureFlags?.withdraw && featureFlags?.deposit ? "grid-cols-2" : "grid-cols-1"}`}
              >
                <FeatureGate flag="withdraw">
                  <TabsTrigger
                    value="withdraw"
                    className={`h-[48px] lg:text-lg text-[14px] font-semibold rounded-full transition-colors focus-visible:ring-0 focus-visible:outline-none data-[state=active]:bg-[#33125F] data-[state=active]:text-white ${
                      selectedTab === "withdraw"
                        ? "bg-[#33125F] text-white"
                        : "text-[#6D6584] bg-transparent"
                    }`}
                  >
                    Withdraw
                  </TabsTrigger>
                </FeatureGate>
                <FeatureGate flag="deposit">
                  <TabsTrigger
                    value="deposit"
                    className={`h-[48px] lg:text-lg text-[14px] font-semibold rounded-full transition-colors focus-visible:ring-0 focus-visible:outline-none data-[state=active]:bg-[#33125F] data-[state=active]:text-white ${
                      selectedTab === "deposit"
                        ? "bg-[#33125F] text-white"
                        : "text-[#6D6584] bg-transparent"
                    }`}
                  >
                    Deposit
                  </TabsTrigger>
                </FeatureGate>
              </TabsList>
            </Tabs>
            {selectedTab === "withdraw" && featureFlags?.withdraw ? (
              <WithdrawPage
                isLoadingFiat={
                  businessId ? isLoadingBusinessWalletAssets : isLoadingFiat
                }
                selectedFiat={selectedFiat}
                loading={loading}
                fiatBalance={fiatBalance}
                fiat={fiatOptions}
                onAmountChange={onAmountChange}
                flagExceed={flagExceed}
                selectedBank={selectedBank}
                amount={amount}
                setAmount={setAmount}
                flagSelectedBank={flagSelectedBank}
                isWithdraw={true}
                bankData={existingbanks}
                onSelect={handleSelecedBank}
                onAddBank={handleAddNewBank}
                normalFee={null}
                instantPayment={instantPayment}
                setInstantPayment={setInstantPayment}
                instantFee={null}
                handleContinue={handleContinue}
                disabled={disabled}
                open={open}
                setOpen={setOpen}
                handleSelect={handleSelect}
                loadingExisting={loadingExisting}
                minWithdrawal={minWithdrawal}
                is2FAEnabled={isEnabled}
              />
            ) : (
              <Deposit
                onDeposit={handleDepositMade}
                bank={depositBank}
                refCode={refCode}
              />
            )}
          </div>
          <SecuredFooter />
        </Card>
      )}
      {showAddBank && (
        <Card className="flex flex-col w-full lg:pt-8 pt-5 !shadow-none bg-white rounded-2xl">
          <div className="lg:px-8 px-5">
            <Tabs
              value={getValidSelectedTab()}
              onValueChange={handleTabChange}
              className="mb-8"
            >
              <TabsList
                className={`grid rounded-full bg-transparent border border-[#EBE9EF] border-[1px] h-[64px] px-[8px] ${featureFlags?.withdraw && featureFlags?.deposit ? "grid-cols-2" : "grid-cols-1"}`}
              >
                <FeatureGate flag="withdraw">
                  <TabsTrigger
                    value="withdraw"
                    className={`h-[48px] lg:text-lg text-[14px] font-semibold rounded-full transition-colors focus-visible:ring-0 focus-visible:outline-none data-[state=active]:bg-[#33125F] data-[state=active]:text-white ${
                      selectedTab === "withdraw"
                        ? "bg-[#33125F] text-white"
                        : "text-[#6D6584] bg-transparent"
                    }`}
                  >
                    Withdraw
                  </TabsTrigger>
                </FeatureGate>
                <FeatureGate flag="deposit">
                  <TabsTrigger
                    value="deposit"
                    className={`h-[48px] lg:text-lg text-[14px] font-semibold rounded-full transition-colors focus-visible:ring-0 focus-visible:outline-none data-[state=active]:bg-[#33125F] data-[state=active]:text-white ${
                      selectedTab === "deposit"
                        ? "bg-[#33125F] text-white"
                        : "text-[#6D6584] bg-transparent"
                    }`}
                  >
                    Deposit
                  </TabsTrigger>
                </FeatureGate>
              </TabsList>
            </Tabs>

            {selectedTab === "withdraw" ? (
              !showDefault &&
              !withdrawalComplete &&
              !twoFA &&
              !viewAccDetails &&
              showAddBank && (
                <WithdrawAddBank
                  userId={userId}
                  onConfirm={handleNewBank}
                  onBack={handleBackFromAddBank}
                  formState={bankFormState}
                  onFormStateChange={handleBankFormStateChange}
                />
              )
            ) : (
              <Deposit
                onDeposit={handleDepositMade}
                bank={depositBank}
                refCode={refCode}
              />
            )}
          </div>
          <SecuredFooter />
        </Card>
      )}
      {viewAccDetails &&
        !withdrawalComplete &&
        !showDefault &&
        !showAddBank &&
        !twoFA && (
          <BankDetails
            onBack={handleBack}
            bankInfo={selectedBank}
            onContinue={handleTwoFA}
            setUp={handleSetup}
          />
        )}
      {twoFA &&
        !withdrawalComplete &&
        !showDefault &&
        !viewAccDetails &&
        !showAddBank && (
          <AuthTwoFA
            onVerify={handleVerify}
            onBack={handleBackfromTwoFA}
            isVerifying={isVerifying}
            setIsVerifying={setIsVerifying}
          />
        )}
      {addComplete &&
        !withdrawalComplete &&
        !confirmWithdrawal &&
        !showDepositMadePage &&
        !showDefault &&
        !viewAccDetails &&
        !twoFA &&
        !showAddBank && <BankAddComplete onDone={handleAddBankComplete} />}
      {withdrawalComplete &&
        !addComplete &&
        !confirmWithdrawal &&
        !showDepositMadePage &&
        !showDefault &&
        !viewAccDetails &&
        !twoFA &&
        !showAddBank && (
          <WithdrawalComplete onDone={handleWithdrawalComplete} />
        )}
      {confirmWithdrawal &&
        !withdrawalComplete &&
        !addComplete &&
        !showDepositMadePage &&
        !showDefault &&
        !viewAccDetails &&
        !twoFA &&
        !showAddBank && (
          <WithdrawConfirm
            onConfirm={handleWithDrawTwoFA}
            data={withdrawalData}
            quoteData={withdrawData}
            bank={selectedBank}
            instantPayment={instantPayment}
            twoFA={isEnabled}
            setUp={handleSetup}
            onBack={handleConfirmBack}
            originalAmount={
              amount != null && amount !== "" ? parseFloat(amount) : undefined
            }
          />
        )}
      {showDepositMadePage &&
        !confirmWithdrawal &&
        !withdrawalComplete &&
        !addComplete &&
        !showDefault &&
        !viewAccDetails &&
        !twoFA &&
        !showAddBank && <DepositMade onDone={handleDepositComplete} />}
      {transactionError &&
        !withdrawalComplete &&
        !addComplete &&
        !showDepositMadePage && (
          <TransactionFailure
            title={transactionError.title}
            message={transactionError.message}
            onTryAgain={
              transactionError.retryable ? handleTransactionErrorRetry : null
            }
            onBack={handleTransactionErrorClose}
            isLoading={isRetrying}
          />
        )}
    </>
  );
}
